import { cn } from "@gymflow/helpers";
import { usePortalRoutes } from "apps/portal/src/hooks";
import { ModalContext, useClubSettings } from "apps/portal/src/providers";
import { AuthenticationContext } from "apps/portal/src/providers/authentication/AuthenticationContext";
import { ReactNode, useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, LinkProps } from "react-router-dom";

import { RouteFeature } from "../../../../routes";
import { Button, ChevronRightIcon } from "../../../atoms";
import CustomTooltip from "../../../atoms/CustomTooltip";
import { Bell01Icon } from "../../../atoms/icons/Bell01Icon";
import { CalendarCheck02Icon } from "../../../atoms/icons/CalendarCheck02Icon";
import { ClassesIcon } from "../../../atoms/icons/ClassesIcon";
import { Coins03Icon } from "../../../atoms/icons/Coins03Icon";
import { CreditCard02Icon } from "../../../atoms/icons/CreditCard02Icon";
import { File02Icon } from "../../../atoms/icons/File02Icon";
import { LogOut01Icon } from "../../../atoms/icons/LogOut01Icon";
import { LogOut02Icon } from "../../../atoms/icons/LogOut02Icon";
import { ReceiptCheckIcon } from "../../../atoms/icons/ReceiptCheckIcon";
import { UserSquareIcon } from "../../../atoms/icons/UserSquareIcon";
import { ConfirmModal } from "../../../templates";

type SectionItem = {
  id: string;
  label: string;
  icon: ReactNode;
  link?: string;
  openLinkInNewTab?: boolean;
  tooltipMessage?: string;
};

type MenuSectionType = {
  id: string;
  title: string;
  sections: SectionItem[];
};

export const HostedPagesProfileMenu = () => {
  const { t } = useTranslation();
  const { logout } = useContext(AuthenticationContext);
  const { createSiteOrEmbedLink } = usePortalRoutes();
  const { setModal, hide: hideModal } = useContext(ModalContext);
  const {
    club: { termsConditions },
  } = useClubSettings();

  const menu: MenuSectionType[] = useMemo(
    () =>
      [
        {
          id: "account",
          title: t(
            "pages.hostedPagesProfile.hostedPagesProfileMenu.menu.account.title",
          ),
          sections: [
            {
              id: "membershipHistory",
              label: t(
                "pages.hostedPagesProfile.hostedPagesProfileMenu.menu.account.options.membershipHistory",
              ),
              icon: (
                <UserSquareIcon
                  className="size-5"
                  pathClassName="stroke-gray-500 dark:stroke-gray-400"
                />
              ),
              link: RouteFeature.SiteProfileMembershipHistory,
            },
            {
              id: "packages",
              label: t(
                "pages.hostedPagesProfile.hostedPagesProfileMenu.menu.account.options.packages",
              ),
              icon: (
                <Coins03Icon
                  className="size-5"
                  pathClassName="stroke-gray-500 dark:stroke-gray-400"
                />
              ),
              link: RouteFeature.SiteProfilePackages,
            },
          ],
        },
        {
          id: "booking",
          title: t(
            "pages.hostedPagesProfile.hostedPagesProfileMenu.menu.booking.title",
          ),
          sections: [
            {
              id: "classes",
              label: t(
                "pages.hostedPagesProfile.hostedPagesProfileMenu.menu.booking.options.classes",
              ),
              icon: (
                <ClassesIcon
                  className="size-5"
                  pathClassName="fill-gray-500 dark:fill-gray-400"
                />
              ),
              link: RouteFeature.SiteProfileBookingClasses,
            },
            {
              id: "appointments",
              label: t(
                "pages.hostedPagesProfile.hostedPagesProfileMenu.menu.booking.options.appointments",
              ),
              icon: (
                <CalendarCheck02Icon
                  className="size-5"
                  pathClassName="stroke-gray-500 dark:stroke-gray-400"
                />
              ),
              link: RouteFeature.SiteProfileBookingAppointments,
            },
          ],
        },
        {
          id: "payment",
          title: t(
            "pages.hostedPagesProfile.hostedPagesProfileMenu.menu.payment.title",
          ),
          sections: [
            {
              id: "paymentMethods",
              label: t(
                "pages.hostedPagesProfile.hostedPagesProfileMenu.menu.payment.options.paymentMethods",
              ),
              icon: (
                <CreditCard02Icon
                  className="size-5"
                  pathClassName="stroke-gray-500 dark:stroke-gray-400"
                />
              ),
              link: RouteFeature.SiteProfilePayments,
            },
            {
              id: "receipts",
              label: t(
                "pages.hostedPagesProfile.hostedPagesProfileMenu.menu.payment.options.receipts",
              ),
              icon: (
                <ReceiptCheckIcon
                  className="size-5"
                  pathClassName="stroke-gray-500 dark:stroke-gray-400"
                />
              ),
              link: RouteFeature.SiteProfileReceipts,
            },
          ],
        },
        {
          id: "preferences",
          title: t(
            "pages.hostedPagesProfile.hostedPagesProfileMenu.menu.preferences.title",
          ),
          sections: [
            {
              id: "notifications",
              label: t(
                "pages.hostedPagesProfile.hostedPagesProfileMenu.menu.preferences.options.notifications",
              ),
              icon: (
                <Bell01Icon
                  className="size-5"
                  pathClassName="stroke-gray-500 dark:stroke-gray-400"
                />
              ),
              tooltipMessage: t(
                "pages.hostedPagesProfile.hostedPagesProfileMenu.tooltips.manageNotifications",
              ),
            },
          ],
        },
        {
          id: "documents",
          title: t(
            "pages.hostedPagesProfile.hostedPagesProfileMenu.menu.documents.title",
          ),
          sections: [
            {
              id: "waiverAndTerms",
              label: t(
                "pages.hostedPagesProfile.hostedPagesProfileMenu.menu.documents.options.waiverAndTerms",
              ),
              icon: (
                <File02Icon
                  className="size-5"
                  pathClassName="stroke-gray-500 dark:stroke-gray-400"
                />
              ),
              link: termsConditions,
              openLinkInNewTab: true,
            },
          ],
        },
      ] satisfies MenuSectionType[],
    [t, termsConditions],
  );

  const onClickLogout = useCallback(() => {
    setModal(
      <ConfirmModal
        type="danger"
        icon={
          <LogOut02Icon className="size-6" pathClassName="stroke-error-600" />
        }
        title={t(
          "pages.hostedPagesProfile.hostedPagesProfileMenu.logoutModal.title",
        )}
        confirmText={t(
          "pages.hostedPagesProfile.hostedPagesProfileMenu.logoutModal.confirmText",
        )}
        onCancel={() => hideModal()}
        onConfirm={async () => {
          await logout();
          hideModal();
        }}
      >
        {t(
          "pages.hostedPagesProfile.hostedPagesProfileMenu.logoutModal.description",
        )}
      </ConfirmModal>,
    );
  }, [hideModal, logout, setModal, t]);

  return (
    <div className="bg-gray-0 flex flex-1 flex-col gap-[1.125rem] overflow-y-auto rounded-[1.25rem] border border-gray-200 p-4 lg:max-w-lg dark:border-gray-600 dark:bg-gray-950">
      {menu.map(({ id: menuId, title, sections }) => (
        <div key={menuId} className="flex flex-col gap-1.5">
          <div className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
            {title}
          </div>
          <div className="bg-gray-0 shadow-xs rounded-2xl border border-gray-200 dark:border-gray-600 dark:bg-gray-950">
            {sections.map(
              ({
                id: sectionId,
                label,
                icon,
                link,
                openLinkInNewTab,
                tooltipMessage,
              }) => {
                const Component = !link
                  ? (props: Partial<React.ComponentPropsWithRef<"div">>) => (
                      <div {...props} />
                    )
                  : !openLinkInNewTab
                    ? ({ children, ...props }: Partial<LinkProps>) => (
                        <Link {...props} to={createSiteOrEmbedLink(link ?? "")}>
                          {children}
                        </Link>
                      )
                    : ({
                        children,
                        ...props
                      }: Partial<React.ComponentPropsWithoutRef<"a">>) => (
                        <a
                          {...props}
                          target="_blank"
                          rel="noreferrer"
                          href={link}
                        >
                          {children}
                        </a>
                      );
                return (
                  <div
                    key={sectionId}
                    className="flex flex-col border-b border-b-gray-300 last:border-none dark:border-b-gray-700"
                  >
                    <CustomTooltip message={tooltipMessage}>
                      <Component
                        className={cn(
                          "flex justify-between py-4 pl-3 pr-2 items-center",
                          {
                            "cursor-pointer hover:opacity-80": link,
                            "cursor-not-allowed opacity-30": !link,
                          },
                        )}
                      >
                        <div className="flex items-center gap-3">
                          {icon}
                          <div className="dark:text-gray-25 text-base font-medium text-gray-950">
                            {label}
                          </div>
                        </div>
                        <ChevronRightIcon
                          className="size-6"
                          pathClassName="dark:stroke-gray-400 stroke-gray-500"
                        />
                      </Component>
                    </CustomTooltip>
                  </div>
                );
              },
            )}
          </div>
        </div>
      ))}
      <Button
        intent="error-outline"
        className="border-error-300 mt-auto w-full gap-2 self-center lg:w-56"
        onClick={() => onClickLogout()}
      >
        <div className="text-error-600 dark:text-error-400 text-base font-semibold">
          {t("pages.hostedPagesProfile.hostedPagesProfileMenu.logout")}
        </div>
        <LogOut01Icon
          className="size-5"
          pathClassName="stroke-error-600 dark:stroke-error-400"
        />
      </Button>
    </div>
  );
};
